import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { SunBucksAuthService } from '../services/sun-bucks-auth.service';
import { SunBucksConfigService } from '../services/sun-bucks-config.service';

/**
 * Handles 401 responses from the YARP SunBucks proxy (/api/proxy/sunbucks/*)
 * by asking the BFF to issue fresh HttpOnly cookies and retrying once.
 *
 * Token injection (Authorization: Bearer, X-Token-Fingerprint) is handled
 * entirely server-side by the YARP cookie-to-header transform.  This
 * interceptor no longer reads, stores, or forwards any token value — the
 * raw JWT is never accessible to JavaScript.
 */
@Injectable()
export class SunBucksJwtInterceptor implements HttpInterceptor {

  constructor(
    private readonly configService: SunBucksConfigService,
    private readonly authService: SunBucksAuthService
  ) { }

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpEvent<unknown>> {

    // Config not loaded yet or not a SunBucks proxy request — pass through.
    if (!this.configService.isLoaded || !req.url.startsWith(this.configService.apiBaseUrl)) {
      return next.handle(req);
    }

    return next.handle(req).pipe(
      catchError((err: unknown) => {
        if (err instanceof HttpErrorResponse && err.status === 401) {
          // Expired or missing cookies — ask the BFF to issue fresh ones,
          // then retry the original request exactly once.
          // A second 401 propagates to the caller — no infinite loop.
          return this.authService.ensureCookies().pipe(
            switchMap(() => next.handle(req))
          );
        }
        return throwError(() => err);
      })
    );
  }
}

